/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */
import React, { useContext } from 'react';
import proptypes from 'prop-types';
import {
  params, string, arrayOf, extend, shape, bool, number, customType, QueryContext
} from '@thd-nucleus/data-sources';

import { useBundleProducts } from '../hooks/useBundleProducts';
import { useBundlePricingDetails } from '../hooks/useBundlePricingDetails';

const { string: propTypeString, bool: propTypeBool } = proptypes;

const dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      itemId: string(),
      pricing: params({ storeId: string(), isBrandPricingPolicyCompliant: bool() }).shape({
        original: number({ float: true }),
        mapAboveOriginalPrice: bool(),
        mapDetail: shape({
          mapPolicy: string(),
          mapOriginalPriceViolation: bool(),
          mapSpecialPriceViolation: bool(),
          effectiveMap: number({ float: true }),
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true })
        }),
        promotion: shape({
          type: string(),
          description: shape({
            shortDesc: string(),
            longDesc: string()
          }),
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true }),
          savingsCenter: string(),
          savingsCenterPromos: string(),
          specialBuySavings: string(),
          specialBuyDollarOff: string(),
          specialBuyPercentageOff: string(),
          experienceTag: string(),
          promotionTag: string(),
          subExperienceTag: string()
        }),
        specialBuy: number({ float: true }),
        value: number({ float: true }),
        bundlePromotionalAdjustments: arrayOf(shape({
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true }),
          promoId: string(),
          promotionTag: string(),
          description: shape({
            shortDesc: string(),
            longDesc: string()
          }),
          dates: shape({
            start: string(),
            end: string()
          })
        }))
      }),
      bundleSpecificationDetails: params({ storeId: string(), zipCode: string() }).shape({
        type: string(),
        brand: string(),
        experienceType: string(),
        accessories: shape({
          categories: arrayOf(shape({
            categoryName: string(),
            offerings: arrayOf(shape({
              offeringName: string(),
              products: arrayOf(shape({
                id: string(),
                defaultProductId: string(),
                type: string(),
                quantity: string()
              }))
            }))
          }))
        }),
        components: arrayOf(shape({
          id: string(),
          defaultProductId: string(),
          quantity: string(),
          type: string(),
          allowSwap: bool(),
          allowRemove: bool(),
          selectableVariants: arrayOf(shape({
            name: string(),
            id: string(),
            guid: string(),
            value: string()
          })),
          priority: number()
        }))
      })
    })
  },
  {
    products: params({
      itemIds: arrayOf(string().isRequired()).isRequired(),
      filterResponse: bool()
    }).arrayOf({
      itemId: string(),
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        productType: string(),
        hideItem: string()
      }),
      availabilityType: shape({
        obsolete: bool(),
        discontinued: bool()
      }),
      info: shape({
        isSponsored: bool(),
        minimumOrderQuantity: number()
      }),
      pricing: params({ storeId: string(), isBrandPricingPolicyCompliant: bool() }).shape({
        original: number({ float: true }),
        mapAboveOriginalPrice: bool(),
        mapDetail: shape({
          mapPolicy: string(),
          mapOriginalPriceViolation: bool(),
          mapSpecialPriceViolation: bool(),
          effectiveMap: number({ float: true }),
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true })
        }),
        promotion: shape({
          type: string(),
          description: shape({
            shortDesc: string(),
            longDesc: string()
          }),
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true }),
          savingsCenter: string(),
          savingsCenterPromos: string(),
          specialBuySavings: string(),
          specialBuyDollarOff: string(),
          specialBuyPercentageOff: string(),
          experienceTag: string(),
          promotionTag: string(),
          subExperienceTag: string()
        }),
        specialBuy: number({ float: true }),
        value: number({ float: true }),
        bundlePromotionalAdjustments: arrayOf(shape({
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true }),
          promoId: string(),
          promotionTag: string(),
          description: shape({
            shortDesc: string(),
            longDesc: string()
          }),
          dates: shape({
            start: string(),
            end: string()
          })
        })),
        promotionalAdjustments: params({
          itemQuantities: arrayOf(
            customType('ItemQuantity').shape({
              itemId: string(),
              quantity: number()
            })
          )
        }).arrayOf(shape({
          promoId: string()
        }))
      })
    })
  },
  {
    checkAvailability: params({
      itemIds: arrayOf(string().isRequired()).isRequired(),
      zipCode: string().isRequired(),
      localStoreId: string().isRequired()
    })
      .shape({
        fulfillmentOptions: arrayOf(shape({
          services: arrayOf(shape({
            deliveryAvailabilityDetail: arrayOf(shape({
              itemId: string(),
              status: string(),
              assortmentStrategy: string()
            })),
            earliestAvailabilityDate: string()
          }))
        }))
      })
  }
);

export const withBundleProducts = (BaseComponent) => {

  const BundleComponent = ({ bundleId, storeId, listenToProductsChange = true, ...rest }) => {
    const { defaultVariables } = useContext(QueryContext) || {};
    const isCustomerIdentified = defaultVariables?.current?.isBrandPricingPolicyCompliant || false;
    const bundleProducts = useBundleProducts(bundleId, storeId, listenToProductsChange, isCustomerIdentified);
    const pricingDetails = useBundlePricingDetails({ ...bundleProducts, isCustomerIdentified });
    return (
      <BaseComponent
        {...bundleProducts}
        {...rest}
        pricingDetails={pricingDetails}
      />
    );
  };

  BundleComponent.dataModel = BaseComponent?.dataModel
    ? extend(dataModel, BaseComponent?.dataModel)
    : dataModel;

  BundleComponent.propTypes = {
    bundleId: propTypeString.isRequired,
    storeId: propTypeString.isRequired,
    listenToProductsChange: propTypeBool
  };

  return BundleComponent;
};